// Hardcoded demo scenarios.
//
// This app no longer runs a live YOLO model. Every "detection" you see is a
// scripted timeline attached to a specific sample video (or, for an
// unrecognised upload, deterministically generated from its filename).
// Nothing here calls a model - it only decides WHEN to show WHAT during
// video playback so the sales demo behaves consistently every single time.
//
// VIDEO_PLAYLIST below mirrors backend/camera_manager.py's VIDEO_PLAYLIST
// exactly (same dates, same cameras, same clip order per day) so every video
// referenced there is reachable from the frontend's camera/date archive.

export const CLASS_META = {
  Burn: { label: 'Burn Damage', color: '#c2410c' },
  Foreign_obj: { label: 'Foreign Object', color: '#b91c1c' },
  Insect: { label: 'Insect Activity', color: '#a16207' },
  MOULD: { label: 'Mould Growth', color: '#7c3aed' },
  discoloration: { label: 'Discoloration', color: '#be185d' },
};

export const CAMERAS = ['CAM-1', 'CAM-2'];

// Every sample video shipped in public/videos - shown as Quick Demo chips
// and in the "Download sample videos" menu, so nothing is hidden.
export const SAMPLE_VIDEOS = [
  { key: 'bread', file: 'bread.mp4', title: 'Bread Sample', subtitle: '1 defect · insect activity near unit U-114' },
  { key: 'pizza_base', file: 'pizza_base.mp4', title: 'Pizza Base Sample', subtitle: '2 defects · scorch marks at start and end of run' },
  { key: 'cheese_cubes', file: 'cheese_cubes.mp4', title: 'Cheese Cubes Sample', subtitle: '1 defect · mould flagged early in the batch' },
  { key: 'drinks', file: 'drinks.mp4', title: 'Drinks Sample', subtitle: '1 defect · surface discoloration flagged' },
  { key: 'cookies', file: 'cookies.mp4', title: 'Cookies Sample', subtitle: '2 defects · foreign material found twice' },
  { key: 'mixed_berries', file: 'mixed_berries.mp4', title: 'Mixed Berries Sample', subtitle: '1 defect · mould on frozen berry punnet' },
  { key: 'sandwich', file: 'sandwich.mp4', title: 'Sandwich Sample', subtitle: '1 defect · flies on sandwich pack' },
  { key: 'chips', file: 'chips.mp4', title: 'Chips Sample', subtitle: '1 defect · plastic shard in potato chips' },
  { key: 'mold_berry', file: 'mold_berry.mp4', title: 'Berry Mould Sample', subtitle: '1 defect · mould on frozen berry punnet' },
];

// startFraction/endFraction are fractions (0-1) of the clip's actual playback
// duration, resolved at runtime once the browser knows the video length -
// this keeps the script correct regardless of how long each clip really is.
//
// bbox / bboxEnd are fractions (0-1) of the VIDEO FRAME (not the on-screen
// player), marking where the red detection box is drawn. If bboxEnd is
// omitted the box is static; if given, the box drifts linearly from bbox to
// bboxEnd across the incident's active window (used for clips with real
// on-screen motion, e.g. a crawling insect or a conveyor pan).
//
// Every bbox/timing value here was measured by extracting real frames from
// the source video (5fps) and reading off where the actual visible defect
// sits - not guessed. See conversation history for the extraction method if
// these ever need re-deriving for a replacement clip.
const SCENARIOS = {
  bread: {
    incidents: [
      {
        class_name: 'Insect', startFraction: 0.30, endFraction: 0.82,
        baseConfidence: 88.6, maxConfidence: 94.1,
        bbox: { x: 0.44, y: 0.49, w: 0.15, h: 0.17 },
        bboxEnd: { x: 0.49, y: 0.53, w: 0.15, h: 0.17 },
        product_id: 'GRN-10432', batch_id: 'B-2603-A', product_name: 'Roasted Peanut Mix 500g',
        line_id: 'Line-2', shift: 'Morning', mfg_date: '28-07-2026', expiry_date: '28-01-2027', unit_number: 'U-114',
      },
    ],
  },

  // sandwich.mp4: two flies on a sandwich pack, camera locked off - static box.
  sandwich: {
    incidents: [
      {
        class_name: 'Insect', startFraction: 0.30, endFraction: 0.75,
        baseConfidence: 87.2, maxConfidence: 93.8,
        bbox: { x: 0.43, y: 0.57, w: 0.17, h: 0.10 },
        product_id: 'GRN-40218', batch_id: 'B-2609-F', product_name: 'Chicken Sandwich Pack',
        line_id: 'Line-4', shift: 'Afternoon', mfg_date: '29-07-2026', expiry_date: '31-07-2026', unit_number: 'U-233',
      },
    ],
  },

  // pizza_base.mp4 shows the same scorched bread at the SAME bottom-right
  // hot-spot twice: clearly from the very first frame through ~1.2s, fading
  // through the middle of the clip, then clearly again from ~3.6s to the
  // end. Both incidents use the same bbox since it's the same physical
  // camera spot.
  pizza_base: {
    incidents: [
      {
        class_name: 'Burn', startFraction: 0.02, endFraction: 0.24,
        baseConfidence: 86.4, maxConfidence: 92.0,
        bbox: { x: 0.47, y: 0.50, w: 0.50, h: 0.49 },
        product_id: 'GRN-77120', batch_id: 'B-2701-C', product_name: 'Multigrain Flour 1kg',
        line_id: 'Line-1', shift: 'Afternoon', mfg_date: '30-07-2026', expiry_date: '30-04-2027', unit_number: 'U-208',
      },
      {
        class_name: 'Burn', startFraction: 0.72, endFraction: 0.96,
        baseConfidence: 88.7, maxConfidence: 93.5,
        bbox: { x: 0.47, y: 0.50, w: 0.50, h: 0.49 },
        product_id: 'GRN-77120', batch_id: 'B-2701-C', product_name: 'Multigrain Flour 1kg',
        line_id: 'Line-1', shift: 'Afternoon', mfg_date: '30-07-2026', expiry_date: '30-04-2027', unit_number: 'U-215',
      },
    ],
  },

  // cheese_cubes.mp4: a single moldy cube enters near the top of frame at
  // t=0 and travels down-and-left as the belt carries it toward the camera,
  // exiting the bottom of frame by ~2s - it does NOT recur later in the clip
  // (there is no second moldy cube after that; the batch is clean for the
  // rest of the run). One incident, animated box tracking that real motion.
  cheese_cubes: {
    incidents: [
      {
        class_name: 'MOULD', startFraction: 0.02, endFraction: 0.38,
        baseConfidence: 90.3, maxConfidence: 96.0,
        bbox: { x: 0.45, y: 0.62, w: 0.14, h: 0.17 },
        bboxEnd: { x: 0.39, y: 0.79, w: 0.15, h: 0.18 },
        product_id: 'GRN-33217', batch_id: 'B-2688-B', product_name: 'Wheat Bran 2kg',
        line_id: 'Line-3', shift: 'Morning', mfg_date: '22-07-2026', expiry_date: '22-01-2027', unit_number: 'U-055',
      },
    ],
  },

  // mixed_berries.mp4 and mold_berry.mp4 are the same underlying footage (a
  // punnet of frozen raspberries with visible white mould, panning
  // right-to-left on a packing line) - same bbox motion, different product
  // metadata.
  mixed_berries: {
    incidents: [
      {
        class_name: 'MOULD', startFraction: 0.08, endFraction: 0.55,
        baseConfidence: 89.1, maxConfidence: 95.4,
        bbox: { x: 0.62, y: 0.50, w: 0.16, h: 0.18 },
        bboxEnd: { x: 0.20, y: 0.50, w: 0.16, h: 0.18 },
        product_id: 'GRN-04102', batch_id: 'B-0410-A', product_name: 'Frozen Mixed Berries 400g',
        line_id: 'Line-5', shift: 'Morning', mfg_date: '18-07-2026', expiry_date: '18-07-2027', unit_number: 'U-301',
      },
    ],
  },
  mold_berry: {
    incidents: [
      {
        class_name: 'MOULD', startFraction: 0.08, endFraction: 0.55,
        baseConfidence: 89.1, maxConfidence: 95.4,
        bbox: { x: 0.62, y: 0.50, w: 0.16, h: 0.18 },
        bboxEnd: { x: 0.20, y: 0.50, w: 0.16, h: 0.18 },
        product_id: 'GRN-04103', batch_id: 'B-0410-B', product_name: 'Frozen Mixed Berries 400g',
        line_id: 'Line-5', shift: 'Morning', mfg_date: '18-07-2026', expiry_date: '18-07-2027', unit_number: 'U-302',
      },
    ],
  },

  // drinks.mp4 pans across a bottling line - the two off-color bottles
  // genuinely travel right-to-left across the frame, so the box drifts to
  // track them.
  drinks: {
    incidents: [
      {
        class_name: 'discoloration', startFraction: 0.28, endFraction: 0.68,
        baseConfidence: 81.7, maxConfidence: 89.9,
        bbox: { x: 0.62, y: 0.47, w: 0.11, h: 0.23 },
        bboxEnd: { x: 0.13, y: 0.47, w: 0.16, h: 0.23 },
        product_id: 'GRN-51290', batch_id: 'B-2715-A', product_name: 'Basmati Rice 5kg',
        line_id: 'Line-2', shift: 'Night', mfg_date: '02-08-2026', expiry_date: '02-08-2027', unit_number: 'U-142',
      },
    ],
  },

  // cookies.mp4: one metal fragment on a dough ball rolling toward camera
  // (growing larger, drifting down-frame) from t=0 until it exits past the
  // bottom edge around 78% in - it never reappears after that. Modeled as
  // two "hits" (a believable confidence dip and reacquire, matching the
  // "found twice" subtitle) rather than one continuous detection, but both
  // windows now sit inside the fragment's actual 0%-78% visible span and
  // track its real drift - the old second incident ran to 94%, well past
  // when the fragment had already left frame, showing a box over nothing.
  cookies: {
    incidents: [
      {
        class_name: 'Foreign_obj', startFraction: 0.02, endFraction: 0.46,
        baseConfidence: 92.4, maxConfidence: 97.2,
        bbox: { x: 0.47, y: 0.50, w: 0.08, h: 0.10 },
        bboxEnd: { x: 0.50, y: 0.68, w: 0.09, h: 0.12 },
        product_id: 'GRN-60874', batch_id: 'B-2699-D', product_name: 'Refined Sugar 1kg',
        line_id: 'Line-1', shift: 'Afternoon', mfg_date: '26-07-2026', expiry_date: '26-07-2028', unit_number: 'U-063',
      },
      {
        class_name: 'Foreign_obj', startFraction: 0.58, endFraction: 0.76,
        baseConfidence: 88.1, maxConfidence: 93.6,
        bbox: { x: 0.48, y: 0.78, w: 0.11, h: 0.16 },
        product_id: 'GRN-60874', batch_id: 'B-2699-D', product_name: 'Refined Sugar 1kg',
        line_id: 'Line-1', shift: 'Afternoon', mfg_date: '26-07-2026', expiry_date: '26-07-2028', unit_number: 'U-077',
      },
    ],
  },

  // chips.mp4: a clear plastic shard drifts diagonally down-and-left across
  // the whole clip as the belt carries it toward camera (visible from the
  // very first frame to the very last) - the box now drifts with it via
  // bboxEnd instead of sitting frozen at the frame-1 position, which used to
  // leave it hovering over clean chips with no shard under it by mid-clip.
  chips: {
    incidents: [
      {
        class_name: 'Foreign_obj', startFraction: 0.02, endFraction: 0.94,
        baseConfidence: 90.7, maxConfidence: 96.1,
        bbox: { x: 0.46, y: 0.36, w: 0.08, h: 0.12 },
        bboxEnd: { x: 0.25, y: 0.76, w: 0.11, h: 0.16 },
        product_id: 'GRN-71190', batch_id: 'B-2822-C', product_name: 'Ridged Potato Chips 150g',
        line_id: 'Line-6', shift: 'Night', mfg_date: '25-07-2026', expiry_date: '25-01-2027', unit_number: 'U-410',
      },
    ],
  },

};

// Mirrors backend/camera_manager.py's VIDEO_PLAYLIST: same dates, same
// cameras, same clip order per day. "today" always resolves to the real
// current date; the other two are fixed reference days (matching the
// original historical demo data) rather than a rolling window, so the
// archive doesn't quietly change meaning depending on when it's opened.
export const VIDEO_PLAYLIST = {
  today: { 'CAM-1': ['bread', 'drinks'], 'CAM-2': ['mixed_berries'] },
  '01-04-2026': { 'CAM-1': ['pizza_base'], 'CAM-2': ['sandwich', 'cookies'] },
  '31-03-2026': { 'CAM-1': ['cheese_cubes'], 'CAM-2': ['chips'] },
};

function formatDateKey(date) {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${date.getFullYear()}`;
}

export function getArchiveDateOptions() {
  const todayLabel = formatDateKey(new Date());
  return [
    { key: 'today', label: `Today (${todayLabel})` },
    { key: '01-04-2026', label: '01-04-2026' },
    { key: '31-03-2026', label: '31-03-2026' },
  ];
}

// Returns the ORDERED list of scenario keys for a camera on a given archive
// date - a real playlist, not a single clip, matching the backend structure.
export function getArchivePlaylist(dateKey, camera) {
  const day = VIDEO_PLAYLIST[dateKey];
  if (!day) return [];
  return day[camera] || [];
}

export function interpolateBox(start, end, progress) {
  if (!start) return null;
  const to = end || start;
  const p = Math.min(1, Math.max(0, progress));
  return {
    x: start.x + (to.x - start.x) * p,
    y: start.y + (to.y - start.y) * p,
    w: start.w + (to.w - start.w) * p,
    h: start.h + (to.h - start.h) * p,
  };
}

function hashString(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) >>> 0;
  }
  return h;
}

const FALLBACK_CLASS_POOL = ['Burn', 'Foreign_obj', 'Insect', 'MOULD', 'discoloration'];

// Any video that isn't one of the bundled samples (e.g. a re-uploaded file
// with a different name) still needs a script. This derives one
// deterministically from the filename, so re-uploading the same file always
// reproduces the same "result" during a live pitch, and roughly 1 in 4
// uploads comes back clean instead of always finding a defect.
function generateFallbackScenario(filename) {
  const h = hashString(filename || 'demo-video');
  if (h % 4 === 0) {
    return { incidents: [] };
  }
  const cls = FALLBACK_CLASS_POOL[h % FALLBACK_CLASS_POOL.length];
  const startFraction = 0.15 + ((h >> 3) % 40) / 100;
  const endFraction = Math.min(0.95, startFraction + 0.25 + ((h >> 7) % 20) / 100);
  const baseConfidence = 80 + (h % 15);
  const maxConfidence = Math.min(99, baseConfidence + 4 + (h % 5));
  const suffix = (h % 900) + 100;
  const bboxX = 0.12 + ((h >> 5) % 55) / 100;
  const bboxY = 0.15 + ((h >> 9) % 55) / 100;
  return {
    incidents: [{
      class_name: cls, startFraction, endFraction, baseConfidence, maxConfidence,
      bbox: { x: bboxX, y: bboxY, w: 0.18, h: 0.18 },
      product_id: `GRN-${suffix}`, batch_id: `B-AUTO-${suffix}`, product_name: 'Uploaded Sample',
      line_id: 'Line-1', shift: 'Auto', mfg_date: '-', expiry_date: '-', unit_number: `U-${suffix}`,
    }],
  };
}

export function resolveScenarioForFilename(filename) {
  const base = (filename || '').toLowerCase().replace(/\.[^.]+$/, '').trim();
  const directHit = SAMPLE_VIDEOS.find(
    (v) => base === v.key || base === v.file.replace(/\.[^.]+$/, '').toLowerCase()
  );
  if (directHit) {
    return { key: directHit.key, label: directHit.title, ...SCENARIOS[directHit.key] };
  }
  const substringHit = SAMPLE_VIDEOS.find((v) => base.includes(v.key));
  if (substringHit) {
    return { key: substringHit.key, label: substringHit.title, ...SCENARIOS[substringHit.key] };
  }
  return { key: `auto-${base || 'upload'}`, label: filename || 'Uploaded video', ...generateFallbackScenario(filename) };
}

export function getScenarioByKey(key) {
  const meta = SAMPLE_VIDEOS.find((v) => v.key === key);
  if (!meta || !SCENARIOS[key]) return null;
  return { key, label: meta.title, file: meta.file, ...SCENARIOS[key] };
}

export function formatClock(seconds) {
  const total = Math.max(0, Math.round(seconds || 0));
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}
